import { useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n/I18nContext';
import { useDealerScopeNavigate } from '../../hooks/useDealerScopeNavigate';
import LanguageSelector from './LanguageSelector';
import omodaLogo from '../../assets/dealers/omoda-logo.png';
import jaecooLogo from '../../assets/dealers/jaecoo-logo.png';
// `?raw` загружает SVG как строку → inline через dangerouslySetInnerHTML.
// Только inline SVG может наследовать `currentColor` от родительского
// `color`. Это даёт нам белый/чёрный логотип через CSS, оставляя
// flag-stripes Люксембурга (хардкод fills в SVG) цветными. См. #1022.
import autodisLogoRaw from '../../assets/dealers/autodis-logo.svg?raw';
import './SalonHeader.css';

/**
 * Плашка локаций (ESCH|MERSCH|WICKRANGE) в лого: делаем knockout-текст —
 * буквы и дивайдеры вырезаются из заливки плашки маской, сквозь них виден
 * фон страницы (Маша 25.06, #1022). Трансформируем raw-строку SVG на уровне
 * компонента, а не в самом .svg-ассете: ассет грузит ещё и print через
 * `<img>`, где knockout дал бы невидимые (прозрачные) буквы на белой бумаге.
 *
 * Структура исходника: <rect> плашки (fill currentColor) + <g> с буквами +
 * 2 <line>-дивайдера, все буквы/линии красятся `var(--autodis-locpill-ink)`.
 * Превращаем буквы/линии в чёрную часть маски (вырез), плашку — в белую
 * (видимую), и навешиваем маску на rect. Если разметка изменится и regexp
 * не сматчит — возвращаем исходный SVG без knockout (graceful fallback).
 */
const PILL_RECT =
  '<rect x="335.45" y="90.09" width="218.27" height="17.77" fill="currentColor" />';

const buildKnockoutLogo = (raw) => {
  const escaped = PILL_RECT.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const block = new RegExp(
    `${escaped}\\s*(<g>[\\s\\S]*?</g>)\\s*(<line[^>]*?/>)\\s*(<line[^>]*?/>)`
  );
  const match = raw.match(block);
  // Нет совпадения по плашке ИЛИ некуда вставить маску (<defs>) — отдаём
  // исходный SVG без knockout. Иначе rect сослался бы на несуществующий
  // `url(#...)` и буквы пропали бы (две .replace() не атомарны).
  if (!match || !raw.includes('</defs>')) return raw;
  const [, glyphs, line1, line2] = match;
  const cut = (s) => s.replace(/var\(--autodis-locpill-ink,\s*#fff\)/g, '#000');
  const mask =
    '<mask id="autodis-locpill-knockout" maskContentUnits="userSpaceOnUse">' +
    '<rect x="335.45" y="90.09" width="218.27" height="17.77" fill="#fff" />' +
    cut(glyphs) +
    cut(line1) +
    cut(line2) +
    '</mask>';
  const maskedRect = PILL_RECT.replace(
    '/>',
    'mask="url(#autodis-locpill-knockout)" />'
  );
  return raw.replace('</defs>', `${mask}</defs>`).replace(block, maskedRect);
};

const autodisLogoKnockout = buildKnockoutLogo(autodisLogoRaw);

/**
 * Общий header для салонного flow Autodis (#1022): Welcome screen,
 * CarLandingPage, ShowroomPage. Структура:
 *
 *   ┌───────────────────────────────────────────────────┐
 *   │  [back-to-salon link]   AUTODIS   [actions slot]  │  ← top row
 *   │             ┌─────────────────────────┐           │
 *   │             │ OMODA      JAECOO       │           │  ← brands row
 *   │             └─────────────────────────┘           │
 *   └───────────────────────────────────────────────────┘
 *
 * @param {object} props
 * @param {('omoda' | 'jaecoo')[]} [props.brands=[]] — какие лого
 *   показать в нижнем ряду. На Welcome screen: [omoda, jaecoo].
 *   На CarLandingPage: [carBrand]. На Showroom: [] (skip row).
 * @param {boolean} [props.showLanguage=true] — рендерить ли
 *   LanguageSelector в actions slot.
 * @param {React.ReactNode} [props.actionsSlot] — extra buttons справа
 *   (например catalog FAB). Идут ПОСЛЕ LanguageSelector.
 *
 * @param {{ src: string, alt: string }} [props.logo] — свой логотип вместо
 *   Autodis (витрина ShiftGears на /showroom, #1441). Без пропа — Autodis.
 * @param {boolean} [props.showLogo=true] — false: только действия справа.
 *   Свайп-лендинг витрины: логотип там уже стоит плашкой над фото, и второй
 *   в шапке ложился на неё.
 * @param {string} [props.homePath='/salon'] — куда ведёт логотип.
 *
 * Кликабельность лого выводится из маршрута: лого ведёт на `homePath`
 * и автоматически становится инертным, когда мы уже там.
 * Страницам передавать флаг не нужно — правило живёт в одном месте.
 */
const BRAND_LOGOS = {
  omoda: { src: omodaLogo, alt: 'OMODA' },
  jaecoo: { src: jaecooLogo, alt: 'JAECOO' },
};

const WELCOME_PATH = '/salon';

export const SalonHeader = ({
  brands = [],
  showLanguage = true,
  actionsSlot,
  logo,
  showLogo = true,
  homePath = WELCOME_PATH,
}) => {
  const location = useLocation();
  // dealer-scope navigate: лого ведёт на /salon с сохранением ?token, иначе
  // reload Welcome теряет дилера → пустой салон (см. useDealerScopeNavigate).
  const dealerNavigate = useDealerScopeNavigate();
  const { t } = useTranslation();

  // Лого = «домой на Welcome», но на самой Welcome (`/salon`) кликать
  // некуда — рендерим статично.
  const logoClickable = location.pathname !== homePath;

  const handleLogoClick = () => {
    if (!logoClickable) return;
    dealerNavigate(homePath);
  };

  const logoImg = logo ? (
    <img src={logo.src} alt={logo.alt} className="salon-header__primary-logo salon-header__primary-logo--img" />
  ) : (
    <span
      role="img"
      aria-label="Autodis"
      className="salon-header__primary-logo"
      dangerouslySetInnerHTML={{ __html: autodisLogoKnockout }}
    />
  );

  return (
    <header className="salon-header">
      <div className="salon-header__top">
        {/* Без логотипа выбор языка встаёт на его место слева, а справа
            остаются действия: логотип посередине — плашка лендинга, и
            кнопки по обе стороны её не задевают (#1441). */}
        {!showLogo ? (
          showLanguage ? <LanguageSelector className="salon-header__lang" /> : <span aria-hidden="true" />
        ) : logoClickable ? (
          <button
            type="button"
            className="salon-header__logo-btn"
            onClick={handleLogoClick}
            aria-label={t('salon.header.toWelcome', 'Go to welcome')}
          >
            {logoImg}
          </button>
        ) : (
          <div className="salon-header__logo-btn salon-header__logo-btn--static">
            {logoImg}
          </div>
        )}

        {((showLanguage && showLogo) || actionsSlot) && (
          <div className="salon-header__actions">
            {showLanguage && showLogo && <LanguageSelector className="salon-header__lang" />}
            {actionsSlot}
          </div>
        )}
      </div>

      {brands.length > 0 && (
        <div
          className="salon-header__brands"
          role="list"
          aria-label={t('salon.aria.brands', 'Salon brands')}
        >
          {brands.map((id) => {
            const logo = BRAND_LOGOS[id];
            if (!logo) return null;
            return (
              <img
                key={id}
                src={logo.src}
                alt={logo.alt}
                className="salon-header__brand-logo"
                role="listitem"
              />
            );
          })}
        </div>
      )}
    </header>
  );
};

export default SalonHeader;
